import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, ChevronRight, UserPlus, FileText } from 'lucide-react';
import { api, type GarageActivityEvent } from '@/lib/api';
import { formatDistanceToNow } from '@/lib/utils';
import { trackGarageActivityWidgetClick } from '@/lib/analytics';

function ActivityItem({ item }: { item: GarageActivityEvent }) {
  const isJoin = item.type === 'join';
  const name = item.displayName || item.username;

  return (
    <Link
      to={`/garage/${item.garageSlug}`}
      onClick={() => trackGarageActivityWidgetClick(isJoin ? 'join_garage' : 'view_post', item.garageId)}
      className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 transition-colors hover:bg-bg-tertiary group"
    >
      <div className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
        isJoin ? 'bg-emerald-500/10 text-emerald-400' : 'bg-violet-500/10 text-violet-400'
      }`}>
        {isJoin ? <UserPlus className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
          <span className="font-medium">{name}</span>
          {isJoin ? (
            <> joined <span className="font-medium">{item.garageEmoji || ''} {item.garageName}</span></>
          ) : (
            <> posted in <span className="font-medium">{item.garageEmoji || ''} {item.garageName}</span></>
          )}
        </p>
        {item.postTitle && (
          <p className="mt-0.5 text-xs text-text-tertiary line-clamp-1">
            {item.postTitle}
          </p>
        )}
        <p className="mt-0.5 text-xs text-text-tertiary">
          {formatDistanceToNow(new Date(item.timestamp))}
        </p>
      </div>
    </Link>
  );
}

export function GarageActivityPulse() {
  const { data, isLoading } = useQuery({
    queryKey: ['garages', 'activity-feed'],
    queryFn: () => api.garages.activityFeed({ limit: 5 }),
    staleTime: 10 * 60 * 1000, // 10 min (matches backend cache)
  });

  // Don't render empty widget
  if (!isLoading && (!data?.items || data.items.length === 0)) return null;

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-emerald-500/5 to-transparent">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10">
          <Activity className="h-4 w-4 text-emerald-500" />
        </div>
        <h3 className="font-semibold text-text-primary">Garage Pulse</h3>
      </div>

      <div className="p-1.5">
        {isLoading ? (
          <div className="space-y-3 p-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-start gap-2.5 animate-pulse">
                <div className="h-6 w-6 rounded-full bg-bg-tertiary flex-shrink-0" />
                <div className="flex-1">
                  <div className="h-3.5 w-full rounded bg-bg-tertiary" />
                  <div className="mt-1.5 h-3 w-20 rounded bg-bg-tertiary" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          data?.items.map((item) => <ActivityItem key={item.id} item={item} />)
        )}
      </div>

      <Link
        to="/explore?tab=garages"
        className="flex items-center justify-center gap-1 border-t border-border-primary py-3 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
      >
        Explore garages
        <ChevronRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
